import { FileEntryFM, parseSize } from '@bm-js/atlascloud-shared'
import { env } from '../utils/env'
import { useApi } from '../utils/useApi'
import MediaItem from './MediaItem'
import FileDropzone from './FileDropzone'
import { useMemo, useState } from 'react'
import Modal from './Modal'
import { useCustomer, usePopup, useToast } from '../utils/hooks'
import { useFetcher } from '../utils/fetcher'
import DeleteConfirmation from './DeleteConfirmation'
import Input from './Input'
import MediaLibraryColumns from './MediaLibraryColumns'
import Loader from './Loader'
import { colors } from '../utils/config'
import Select from './Select'
import MediaSpotlight from './MediaSpotlight'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useTr } from '../utils/useTr'

type MediaLibraryProps = {
  handleSelect?: (file?: FileEntryFM) => void
  fixedType?: 'image' | 'video' | 'pdf'
  enableUsageQuota?: boolean
  inModal?: boolean
}

const MediaLibrary = ({ inModal = true, ...props }: MediaLibraryProps) => {
  const { tr } = useTr()

  if (inModal)
    return (
      <Modal
        zIndex={100}
        rootId='media-library-modal-root'
        title={tr(TrKey.mediaRouteTitle)}
        onClose={() => props.handleSelect?.()}
      >
        <MediaLibraryInner {...props} />
      </Modal>
    )

  return <MediaLibraryInner {...props} />
}

const MediaLibraryInner = ({
  handleSelect,
  fixedType,
  enableUsageQuota,
}: MediaLibraryProps) => {
  const { closePopup, setPopup } = usePopup()
  const { tr } = useTr()
  const customer = useCustomer()
  const fetcher = useFetcher()
  const toast = useToast()

  const [search, setSearch] = useState('')
  const [typeQuery, setTypeQuery] = useState<string | undefined>(fixedType)
  const [spotlight, setSpotlight] = useState<FileEntryFM | null>(null)

  const typeQueryMap: Record<'image' | 'video' | 'pdf' | '', string> = {
    '': tr(TrKey.mediaTypeAll),
    image: tr(TrKey.mediaTypeImage),
    pdf: tr(TrKey.mediaTypePdf),
    video: tr(TrKey.mediaTypeVideo),
  }

  const {
    data = { files: [], totalSize: 0 },
    refetch,
    isLoading,
    isError,
  } = useApi<{
    files: FileEntryFM[]
    totalSize: number
  }>(`files/list/${typeQuery ? `?type=${typeQuery}` : ''}`)

  const filteredFiles = useMemo(() => {
    if (!search) return data.files
    return data.files.filter((file) =>
      file.name
        .normalize('NFD')
        .toLocaleLowerCase()
        .includes(search.normalize('NFD').toLocaleLowerCase())
    )
  }, [data.files, search])

  const sortedFiles = useMemo(() => {
    return filteredFiles.sort((a, b) => {
      return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    })
  }, [filteredFiles])

  const showUsageQuota = useMemo(() => {
    return enableUsageQuota && customer?.allowedFileUsageInGB
  }, [enableUsageQuota, customer])

  const noFilesContent = useMemo(() => {
    if (isError) {
      return <p className='m0'>{tr(TrKey.mediaFailedToFetchFiles)}</p>
    }
    if (isLoading)
      return (
        <div className='flex justify-center items-center h-full'>
          <Loader color={colors.primary400} />
        </div>
      )
    if (sortedFiles.length) return null
    return <p className='m0'>{tr(TrKey.mediaNoFilesFound)}</p>
  }, [isLoading, sortedFiles, isError, tr])

  const deleteFileInit = (file: FileEntryFM) => {
    setPopup(
      <DeleteConfirmation
        item={file}
        deleteKey='name'
        handleDelete={deleteFile}
        handleCancel={closePopup}
        name={tr(TrKey.mediaTheFile)}
      />
    )
  }

  const deleteFile = async (file: FileEntryFM) => {
    const { err } = await fetcher({
      path: `files/${file.uuid}`,
      method: 'DELETE',
    })

    if (err) return toast.error(tr(TrKey.mediaCouldNotDeleteFile))
    await refetch()
    toast.success(tr(TrKey.mediaFileDeleted))
    closePopup()
  }

  const openFile = (file: FileEntryFM) => {
    const url = `${env.s3FilesBase}/uploads/${file.uuid}`
    window.open(url, '_blank')
  }

  return (
    <div>
      <div className='mb-4'>
        {showUsageQuota && (
          <p className='m-0 mt-1'>
            {tr(TrKey.mediaUsageQuota, {
              used: parseSize(data.totalSize),
              total: customer?.allowedFileUsageInGB?.toString() ?? '0',
            })}
          </p>
        )}
      </div>
      <FileDropzone
        onUploadSuccess={async () => {
          await refetch()
        }}
      />
      <div className='flex gap-2 justify-stretch'>
        <Input
          className='flex-grow'
          type='search'
          placeholder={tr(TrKey.mediaSearchFileName)}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          className='flex-grow'
          disabled={!!fixedType}
          value={typeQuery}
          onChange={(e) => setTypeQuery(e.target.value)}
        >
          {Object.entries(typeQueryMap).map(([k, v]) => (
            <option value={k} key={k}>
              {v}
            </option>
          ))}
        </Select>
      </div>

      <MediaLibraryColumns
        contents={[
          null,
          <p>{tr(TrKey.name)}</p>,
          <p>{tr(TrKey.size)}</p>,
          <p>{tr(TrKey.type)}</p>,
          <p>{tr(TrKey.mediaUploadedAt)}</p>,
          null,
        ]}
      />
      {noFilesContent}
      <div className='rounded-lg overflow-hidden'>
        {sortedFiles.map((file, index) => (
          <MediaItem
            key={file.uuid}
            file={file}
            handleSelect={handleSelect}
            setSpotlight={setSpotlight}
            openFile={openFile}
            handleDelete={deleteFileInit}
            index={index}
          />
        ))}
      </div>

      {spotlight && (
        <MediaSpotlight spotlight={spotlight} setSpotlight={setSpotlight} />
      )}
    </div>
  )
}

export default MediaLibrary
